import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FaPlusCircle } from "react-icons/fa";
import { useHistory } from "react-router-dom";
import Swal from "sweetalert2";
import InputSelectAsync from "../../../../../components/InputSelectAsync";
import { Loading } from "../../../../../components/Loading";
import { BrComerceApiService } from "../../../../../services/BrComerceApiService";
import { intersectionArraysForAdd, intersectionArraysForDeletion } from "../../../../../utils/intersectionArrays";
import useGetWindowWidth from "../../../../../utils/useGetWindowWidth";
import Container from "../../../components/Container";
import { PackageCard } from "../../../components/PackageCard";
import { ScanModal } from "../../../components/ScanModal";
import styles from "./styles.module.scss";
import { MdClear } from "react-icons/md";
import debounce from "../../../../../utils/debounce";


const EditRoutes = () => {
  const api = new BrComerceApiService();
  const history = useHistory();

  const [loading, setLoading] = useState(false);

  const [route, setRoute] = useState({});
  const [routeId, setRouteId] = useState();

  const [driver, setDriver] = useState();
  const [selectedDriver, setSelectedDriver] = useState(null);

  const [routeShippings, setRouteShippings] = useState([]);
  const [newRouteShippings, setNewRouteShippings] = useState([]);
  const [deleteRouteShippings, setDeleteRouteShippings] = useState([]);

  const widthWindows = useGetWindowWidth();
  const [shippingsExpand, setShippingsExpand] = useState([]);
  const [addPackageModal, setAddPackageModal] = useState(false);
  const [originalPackages, setOriginalPackages] = useState([]);

  const [packagesFiltered, setPackagesFiltered] = useState([]);
  const [search, setSearch] = useState("");

  const handleFormSubmit = useCallback(
    async e => {
      e.preventDefault();
      let newAddresses;
      let removeAddresses;

      if (newRouteShippings?.length) {
        const objectsToAdd = intersectionArraysForAdd(originalPackages, newRouteShippings);
        newAddresses = objectsToAdd.map((s) => {
          return {
            id_shipping: s.id
          }
        });
      };

      if (deleteRouteShippings?.length) {
        const objectsToDelete = intersectionArraysForDeletion(originalPackages, deleteRouteShippings);
        removeAddresses = objectsToDelete.map((s) => {
          return {
            id_shipping: s.id
          }
        })
      }

      try {
        setLoading(true);

        const data = {
          driverSub: selectedDriver?.sub,
          ...(newAddresses?.length && { newAddresses }),
          ...(removeAddresses?.length && { removeAddresses })
        };

        await api.makeHttpRequest({
          method: "PUT",
          url: `/route-admin/${routeId}`,
          data
        });

        Swal.fire({
          title: "Sucesso!",
          text: "Rota editada com sucesso!",
          icon: "success",
          showCancelButton: false,
          confirmButtonText: "Ok",
          confirmButtonColor: "#409E6A"
        }).then(() => {
          history.push("/listagem-rotas");
        });
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Erro",
          text: "Não foi possível editar a rota!",
          confirmButtonText: "Ok",
          confirmButtonColor: "#CA1818"
        });
      } finally {
        setLoading(false);
      }
    },
    [
      api,
      history,
      deleteRouteShippings,
      newRouteShippings,
      originalPackages,
      selectedDriver?.sub,
      routeId,
    ]
  );

  const handlePackageExpand = useCallback(
    pack => {
      const find = shippingsExpand.find(p => p.id === pack.id);
      if (find) {
        const newPacks = shippingsExpand.filter(p => p.id !== pack.id);
        setShippingsExpand([...newPacks]);
      } else {
        shippingsExpand.push(pack);
        setShippingsExpand([...shippingsExpand]);
      }
    },
    [shippingsExpand]
  );

  const handleRemoveShipping = useCallback(
    shippingId => {
      if (routeShippings.length === 1) {
        Swal.fire({
          title: "Erro",
          text:
            "A rota deve conter pelo menos uma entrega",
          icon: "error"
        });
        return;
      };

      Swal.fire({
        title: "Deletar objeto",
        text: "Você realmente deseja deletar este objeto da rota?",
        icon: "warning",
        showCancelButton: true,
        cancelButtonText: "Cancelar",
        confirmButtonText: "Sim, fazer a deleção!",
        reverseButtons: true
      }).then((response) => {
        if (response?.value) {
          const pack = routeShippings.find(pack => pack.id === shippingId);

          setDeleteRouteShippings(prevState => [...prevState, pack]);
          setRouteShippings(prevPackages => prevPackages.filter(pack => pack.id !== shippingId));
          setNewRouteShippings(prevState => prevState?.filter(pv => pv.id !== shippingId));
        }
      })
    },
    [routeShippings]
  );

  const debouncedFilter = useMemo(() => debounce((searchString, routeShippings, setPackagesFiltered) => {
    const packagesFiltered = routeShippings.filter(pack => pack?.sro?.includes(searchString) || pack.user?.userData[1]?.Value?.toUpperCase()?.includes(searchString));
    setPackagesFiltered(packagesFiltered);
  }, 500), []);

  const handleSearch = useCallback(() => {
    const searchString = search.trim().toUpperCase();
    debouncedFilter(searchString, routeShippings, setPackagesFiltered);
  }, [routeShippings, search, debouncedFilter]);

  useEffect(() => {
    if (!!search?.length) {
      handleSearch(search);
    } else {
      setPackagesFiltered(routeShippings);
    }
  }, [handleSearch, search, routeShippings])

  useEffect(() => {
    if (routeId) {
      setLoading(true);
      api
        .makeHttpRequest({
          method: "GET",
          url: `route-admin/${routeId}`,
          params: {
            page: 1,
            resultsPerPage: 100,
          }
        })
        .then(response => {
          setRoute(response?.route);
          setLoading(false);
          const packs = response?.routeShippings?.results.map((rs) => rs.shipping)
          setRouteShippings(packs);
          setOriginalPackages(packs);
        })
        .catch(err => {
          console.log(err);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [routeId]);

  useEffect(() => {
    setRouteId(history.location.pathname.split("/")[2]);
    window.setPageTitle("Editar Rota");
  }, [history]);

  useEffect(() => {
    if (route) {
      if (route?.status?.id && route?.status?.id !== 1) {
        Swal.fire({
          icon: "error",
          title: "Erro",
          text: "A rota não pode ser alterada após ser iniciada!",
          confirmButtonText: "Ok",
          confirmButtonColor: "#CA1818"
        }).then(() => history.push("/listagem-rotas"));
      }
      setLoading(true);
      const driver = route?.driver

      if (driver) {
        setDriver({
          label: driver?.name,
          sub: driver?.sub,
          value: driver?.id,
        })
      }

      setLoading(false);
    }
  }, [route]);

  return (
    <>
      <Loading loading={loading} />

      <ScanModal
        setLoading={setLoading}
        packages={routeShippings}
        setPackages={setRouteShippings}
        addPackageModal={addPackageModal}
        setAddPackageModal={setAddPackageModal}
        setNewRouteShippings={setNewRouteShippings}
        deleteRouteShippings={deleteRouteShippings}
        setDeleteRouteShippings={setDeleteRouteShippings}
      />

      <Container title={`Rota n° ${routeId}`}>

        <form className={styles.form} onSubmit={handleFormSubmit}>
          <div className={styles.formContent}>

            {route && <div className={styles.row}>
              <div className={styles.infoCard}>
                <div className={styles.shortInfos}>
                  {route?.status?.name && <div className={styles.info}>
                    <strong>Status:</strong>
                    <span>{route?.status?.name ?? ""}</span>
                  </div>}
                </div>

                <div className={styles.infoAddress}>
                  <div>
                    <strong>Origem: {" "}</strong>
                    <p>{route?.origin_destination?.origin?.address ?? ""}</p>

                  </div>
                  <div>
                    <strong>Destino:</strong>{" "}
                    <p>{route?.origin_destination?.destination?.address ?? ""}</p>
                  </div>
                </div>
              </div>
            </div>
            }

            <div className={`${styles.row} ${styles.driverInput}`}>
              <div className={`${styles.inputContainer}`}>
                <strong>Motorista</strong>
                <div
                  className={`${styles.selectInput}`}
                >
                  <InputSelectAsync
                    userParenting={driver}
                    search={selectedDriver}
                    setSearch={setSelectedDriver}
                    listUserOrDriver={"driver"}
                    placeholder={"Selecione o motorista"}
                  />
                </div>
              </div>
            </div>

            <strong>{`Total de Pacotes ${!!routeShippings?.length ? `(${String(routeShippings?.length).padStart(2, '0')})` : ""}`}</strong>

            <div className={styles.containerSearch}>

              <div className={`${styles.inputContainer} ${styles.inputContainerSmall}`}>
                <div className={styles.input}>
                  <input
                    type="text"
                    name="search"
                    placeholder="Busque pelo SRO ou Cliente"
                    onChange={e => setSearch(e.target.value)}
                    value={search}
                  />

                  {!!search?.length && (
                    <MdClear
                      style={{ cursor: "pointer" }}
                      onClick={() => setSearch("")}
                    />
                  )}
                </div>
              </div>

              <div className={styles.actionsButton}>
                <button
                  type="button"
                  onClick={() => setAddPackageModal(true)}
                  className={`${styles.package}`}
                  disabled={!selectedDriver?.sub}
                >
                  <FaPlusCircle size={16} color="#FFFFFF" />
                  <span>Adicionar pacotes</span>
                </button>
              </div>
            </div>

            <div className={styles.collectWrapper}>
              {!!packagesFiltered?.length ? (

                <div className={styles.packageContainer}>
                  <div className={styles.packagesWrapper}>
                    {packagesFiltered?.map(pack => (
                      <PackageCard
                        widthWindows={widthWindows}
                        key={pack.id}
                        shipping={pack}
                        isExpand={shippingsExpand.find(
                          packExpand => (pack.id) === packExpand.id
                        )}
                        onExpand={() => handlePackageExpand(pack)}
                        onDelete={() => handleRemoveShipping(pack.id)}
                      />
                    ))}
                  </div>
                </div>
              ) : (
                <div className={styles.notFoundOrEmptyContainer}>
                  <p>
                    {
                      !packagesFiltered?.length && search
                        ? "Não foram encontradas envios para essa pesquisa."
                        : "Não foram encontradas envios para Rota."
                    }
                  </p>
                </div>
              )}

            </div>
          </div>

          <div className={styles.buttonsContainer}>
            <div className={styles.buttonContainerBack}>
              <button type="button" onClick={() => history.push("/listagem-rotas")}>
                Voltar
              </button>
            </div>
            <div className={styles.buttonContainer}>
              <button type="submit" disabled={
                !selectedDriver
                || !routeShippings?.length
              }>{"Atualizar"}</button>
            </div>
          </div>
        </form>
      </Container>
    </>
  );
};

export { EditRoutes };